import { Injectable } from '@angular/core';
import { DynamicEventPacket, EventMap, EventPacketUnion, isEventOfType } from '@shared/interfaces/event-bus';
import { filter, map, Observable, Subject } from 'rxjs';

// Optional validator type: returns true if payload is valid
export type ValidatorFn<T> = (payload: unknown) => payload is T;


@Injectable({
  providedIn: 'root'
})

export class EventbusService {
  private event$ = new Subject<EventPacketUnion | DynamicEventPacket<unknown>>();
  // Store last events per type
  // Strongly typed map for typed events
  private lastTypedEvents = new Map<keyof EventMap, EventMap[keyof EventMap]>();
  // Strongly typed map for dynamic events
  private lastDynamicEvents = new Map<string, unknown>();

  // --------------------------------------------------
  // Emit Typed Event
  // --------------------------------------------------
  emitTyped<K extends keyof EventMap>(type: K, payload: EventMap[K]): void {
    this.lastTypedEvents.set(type, payload);
    this.event$.next({ type, payload } as EventPacketUnion);
  }

  // Manually retrieve previous event when needed
  getLastTyped<K extends keyof EventMap>(type: K): EventMap[K] | undefined {
    return this.lastTypedEvents.get(type) as EventMap[K] | undefined;
  }

  onTyped<K extends keyof EventMap>(type: K): Observable<EventMap[K]> {
    return this.event$.pipe(
      filter((evt): evt is EventPacketUnion => isEventOfType(evt, type)),
      map(evt => evt.payload as EventMap[K])
    );
  }

  // --------------------------------------------------
  // Emit Dynamic Event
  // --------------------------------------------------
  emitDynamic<T>(type: string, payload: T): void {
    this.lastDynamicEvents.set(type, payload);
    this.event$.next({ type, payload } as DynamicEventPacket<T>);
  }

  onDynamic<T = unknown>(
    type: string,
    validator?: ValidatorFn<T>
  ): Observable<T> {
    return this.event$.pipe(
      filter(
        (evt): evt is DynamicEventPacket<unknown> =>
          typeof evt.type === 'string' && evt.type === type
      ),
      map(evt => {
        const payload = evt.payload;
        if (!validator) return payload as T;
        if (validator(payload)) return payload as T;
        throw new Error(`Event payload validation failed for ${type}`);
      })
    );
  }

  getLastDynamic<T = unknown>(type: string): T | undefined {
    return this.lastDynamicEvents.get(type) as T | undefined;
  }

  // --------------------------------------------------
  // Clear last event
  // --------------------------------------------------
  clearLastEvent(): void {
    // Overwrite last event with a NO-OP marker
    // This clears ReplaySubject's buffer safely
    this.lastTypedEvents.clear();
    this.lastDynamicEvents.clear();
    this.event$.next({ type: '__CLEARED__', payload: null });
  }

  clearSelectiveLastEvent(excludeTypes: (keyof EventMap)[] = []): void {
    const preserved = new Map<keyof EventMap, any>();
    excludeTypes.forEach(type => {
      const val = this.lastTypedEvents.get(type);
      if (val !== undefined) preserved.set(type, val);
    });

    this.lastTypedEvents.clear();
    this.lastDynamicEvents.clear();

    preserved.forEach((val, type) => {
      this.lastTypedEvents.set(type, val);
    });

    this.event$.next({ type: '__CLEARED__', payload: null } as any);
  }


  clearLastTyped<T extends keyof EventMap>(type: T): void {
    this.lastTypedEvents.delete(type);
  }

  clearLastDynamic(type: string): void {
    this.lastDynamicEvents.delete(type);
  }
  // --------------------------------------------------
  // Raw Stream
  // --------------------------------------------------
  rawEvents(): Observable<EventPacketUnion | DynamicEventPacket<unknown>> {
    return this.event$.asObservable();
  }
}
